import type {
  CreateApInvoiceRequest,
  CreateArInvoiceRequest,
  CreateFinanceAccountRequest,
  FinanceCommandResponse,
  FinanceOverviewResponse,
  InitializeFinanceRequest,
  PostManualJournalRequest,
  RecordFinancePaymentRequest,
  ReverseFinanceJournalRequest,
  UpdateFinancePeriodRequest
} from '@fmcgbyalex/contracts/finance';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from 'react';
import { ApiError, FmcgApiClient, createIdempotencyKey } from '../lib/api-client';
import { useApplication } from './ApplicationProvider';

export type FinanceNotice = Readonly<{
  tone: 'success' | 'error' | 'info';
  title: string;
  detail: string;
}>;

export type FinanceContextValue = Readonly<{
  overview: FinanceOverviewResponse | null;
  loading: boolean;
  refreshing: boolean;
  busyAction: string | null;
  error: ApiError | null;
  notice: FinanceNotice | null;
  refresh: () => Promise<void>;
  dismissNotice: () => void;
  initialize: (input: InitializeFinanceRequest) => Promise<FinanceCommandResponse>;
  createAccount: (input: CreateFinanceAccountRequest) => Promise<FinanceCommandResponse>;
  updatePeriod: (periodId: string, input: UpdateFinancePeriodRequest) => Promise<FinanceCommandResponse>;
  postJournal: (input: PostManualJournalRequest) => Promise<FinanceCommandResponse>;
  reverseJournal: (journalId: string, input: ReverseFinanceJournalRequest) => Promise<FinanceCommandResponse>;
  createApInvoice: (input: CreateApInvoiceRequest) => Promise<FinanceCommandResponse>;
  createArInvoice: (input: CreateArInvoiceRequest) => Promise<FinanceCommandResponse>;
  recordPayment: (input: RecordFinancePaymentRequest) => Promise<FinanceCommandResponse>;
}>;

const FinanceContext = createContext<FinanceContextValue | null>(null);

export function FinanceProvider({ children }: PropsWithChildren) {
  const application = useApplication();
  const [overview, setOverview] = useState<FinanceOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<FinanceNotice | null>(null);
  const generation = useRef(0);

  const client = useMemo(
    () => application.authentication
      ? new FmcgApiClient(application.runtime, application.authentication)
      : null,
    [application.authentication, application.runtime]
  );

  useEffect(() => {
    if (application.status !== 'ready' || !application.tenantId || !application.session || !client) {
      ++generation.current;
      setOverview(null);
      setLoading(false);
      setError(null);
      return;
    }
    void loadFinance(client, application.tenantId, true);
  }, [application.status, application.tenantId, application.session, client]);

  async function loadFinance(activeClient: FmcgApiClient, tenantId: string, initial: boolean): Promise<void> {
    const currentGeneration = ++generation.current;
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError(null);

    const moduleEnabled = application.session?.modules.some(
      (module) => module.key === 'finance' && module.enabled
    ) ?? false;
    const permissions = new Set(application.session?.permissions ?? []);
    const readable = moduleEnabled && permissions.has('finance.reports.read');

    try {
      const next = readable
        ? await activeClient.get<FinanceOverviewResponse>('/v1/finance/overview', tenantId)
        : null;
      if (currentGeneration !== generation.current) return;
      setOverview(next);
    } catch (caught) {
      if (currentGeneration !== generation.current) return;
      const apiError = toApiError(caught);
      setError(apiError);
      setNotice({
        tone: 'error',
        title: apiError.problem.title,
        detail: apiError.problem.detail ?? 'Finance data could not be loaded.'
      });
    } finally {
      if (currentGeneration === generation.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }

  async function refresh(): Promise<void> {
    const activeClient = requireClient();
    const tenantId = requireTenantId();
    await loadFinance(activeClient, tenantId, false);
  }

  async function runMutation<T>(
    label: string,
    successTitle: string,
    successDetail: string,
    operation: (activeClient: FmcgApiClient, tenantId: string) => Promise<T>
  ): Promise<T> {
    const activeClient = requireClient();
    const tenantId = requireTenantId();
    setBusyAction(label);
    setNotice(null);
    try {
      const response = await operation(activeClient, tenantId);
      await loadFinance(activeClient, tenantId, false);
      setNotice({ tone: 'success', title: successTitle, detail: successDetail });
      return response;
    } catch (caught) {
      const apiError = toApiError(caught);
      setError(apiError);
      setNotice({
        tone: 'error',
        title: apiError.problem.title,
        detail: apiError.problem.detail ?? `Request failed${apiError.correlationId ? ` · ${apiError.correlationId}` : ''}`
      });
      throw apiError;
    } finally {
      setBusyAction(null);
    }
  }

  function requireClient(): FmcgApiClient {
    if (!client) throw new Error('Authentication is required.');
    return client;
  }

  function requireTenantId(): string {
    if (!application.tenantId) throw new Error('A tenant must be selected.');
    return application.tenantId;
  }

  const value: FinanceContextValue = {
    overview,
    loading,
    refreshing,
    busyAction,
    error,
    notice,
    refresh,
    dismissNotice: () => setNotice(null),
    initialize: (input) => runMutation(
      'Initializing finance',
      'Finance initialized',
      'The standard chart of accounts and monthly fiscal periods are ready.',
      (activeClient, tenantId) => activeClient.mutate<FinanceCommandResponse>(
        'POST', '/v1/finance/initialize', input, tenantId, createIdempotencyKey('finance-initialize')
      )
    ),
    createAccount: (input) => runMutation(
      'Creating finance account',
      'Finance account created',
      `${input.code} is now available for controlled posting.`,
      (activeClient, tenantId) => activeClient.mutate<FinanceCommandResponse>(
        'POST', '/v1/finance/accounts', input, tenantId, createIdempotencyKey('finance-account-create')
      )
    ),
    updatePeriod: (periodId, input) => runMutation(
      'Updating fiscal period',
      'Fiscal period updated',
      `The period is now ${input.status}.`,
      (activeClient, tenantId) => activeClient.mutate<FinanceCommandResponse>(
        'PATCH', `/v1/finance/periods/${encodeURIComponent(periodId)}`, input, tenantId,
        createIdempotencyKey('finance-period-update')
      )
    ),
    postJournal: (input) => runMutation(
      'Posting journal',
      'Journal posted',
      'The balanced entry is immutable and included in the trial balance.',
      (activeClient, tenantId) => activeClient.mutate<FinanceCommandResponse>(
        'POST', '/v1/finance/journals', input, tenantId, createIdempotencyKey('finance-journal-post')
      )
    ),
    reverseJournal: (journalId, input) => runMutation(
      'Reversing journal',
      'Reversal posted',
      'A linked opposite journal was posted without editing the original entry.',
      (activeClient, tenantId) => activeClient.mutate<FinanceCommandResponse>(
        'POST', `/v1/finance/journals/${encodeURIComponent(journalId)}/reversal`, input,
        tenantId, createIdempotencyKey('finance-journal-reverse')
      )
    ),
    createApInvoice: (input) => runMutation(
      'Posting supplier invoice',
      'Accounts payable invoice posted',
      'The matched procurement invoice now reconciles to the AP control account.',
      (activeClient, tenantId) => activeClient.mutate<FinanceCommandResponse>(
        'POST', '/v1/finance/ap-invoices', input, tenantId, createIdempotencyKey('finance-ap-create')
      )
    ),
    createArInvoice: (input) => runMutation(
      'Posting customer invoice',
      'Accounts receivable invoice posted',
      'The sales order now reconciles to revenue, tax and the AR control account.',
      (activeClient, tenantId) => activeClient.mutate<FinanceCommandResponse>(
        'POST', '/v1/finance/ar-invoices', input, tenantId, createIdempotencyKey('finance-ar-create')
      )
    ),
    recordPayment: (input) => runMutation(
      'Recording payment',
      'Payment allocated',
      'Cash, control account and open-item balances were updated together.',
      (activeClient, tenantId) => activeClient.mutate<FinanceCommandResponse>(
        'POST', '/v1/finance/payments', input, tenantId, createIdempotencyKey('finance-payment-record')
      )
    )
  };

  return <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>;
}

export function useFinance(): FinanceContextValue {
  const context = useContext(FinanceContext);
  if (!context) throw new Error('useFinance must be used within FinanceProvider.');
  return context;
}

function toApiError(caught: unknown): ApiError {
  if (caught instanceof ApiError) return caught;
  return new ApiError(
    0,
    {
      type: 'https://fmcgbyalex.com/problems/client-error',
      title: 'The finance request failed',
      status: 0,
      detail: caught instanceof Error ? caught.message : 'An unexpected client error occurred.'
    },
    null
  );
}
